import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import slide3img from '../aseets/images/home/slide3.jpg';
import { getDeliverables } from '../services/apiService';
import { getFileUrl } from '../services/apiService';

import './WorkPackages.css';

const WorkPackages = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [expandedWP, setExpandedWP] = useState(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [showScrollArrow, setShowScrollArrow] = useState(false);
  const [deliverables, setDeliverables] = useState([]);
  const [deliverablesLoading, setDeliverablesLoading] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1360);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Afficher la flèche si on scroll au-delà de la hauteur de la fenêtre
      setShowScrollArrow(scrollY > windowHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Récupérer les deliverables depuis l'API
  useEffect(() => {
    const fetchDeliverables = async () => {
      try {
        setDeliverablesLoading(true);
        const data = await getDeliverables();
        setDeliverables(data || []);
      } catch (error) {
        console.error('Erreur lors de la récupération des deliverables:', error);
        setDeliverables([]);
      } finally {
        setDeliverablesLoading(false);
      }
    };

    fetchDeliverables();
  }, []);

  const workPackages = [
    {
      id: 'Wp0',
      title: 'Project Coordination and Management',
      description: 'The main goal of WP0 is to ensure smooth coordination, financial management, and reporting throughout the duration of the MedAgriCycle project. This work package aims to maintain effective communication between all partners, manage administrative tasks, and ensure the project stays on track according to timelines and budget.',
      leader: 'ERU (Türkiye)',
      duration: 'Months 1–36',
      icon: '⚡',
      color: '#e74c3c',
      gradient: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
      date: '2024-01-01',
      progress: 85,
      status: 'active',
      contributingPartners: 'All partners',
      tasks: [
        { id: 'T0.1', title: 'Project Coordination and Governance', description: 'ERU will oversee the day-to-day coordination of the project, ensuring that all partners meet deadlines, manage resources efficiently, and comply with financial regulations.', duration: 'Months 1–36', leadPartner: 'ERU (Türkiye)', contributingPartners: 'All partners', completed: true },
        { id: 'T0.2', title: 'Financial Management and Reporting', description: 'ERU will ensure accurate financial reporting in line with PRIMA guidelines, including preparation and submission of periodic and final financial reports.', duration: 'Months 1–36', leadPartner: 'ERU (Türkiye)', contributingPartners: 'All partners', completed: true },
        { id: 'T0.3', title: 'Communication Between Partners', description: 'Establish communication protocols and tools for regular updates, meetings, and feedback among all partners through online meetings and workshops.', duration: 'Months 1–36', leadPartner: 'ERU (Türkiye)', contributingPartners: 'All partners', completed: true },
        { id: 'T0.4', title: 'Risk Management and Mitigation', description: 'Develop and maintain a risk management plan throughout the project, identifying potential risks and developing mitigation strategies.', duration: 'Months 1–36', leadPartner: 'ERU (Türkiye)', contributingPartners: 'All partners', completed: false },
        { id: 'T0.5', title: 'Submission of Deliverables and Progress Reports', description: 'Ensure timely submission of all required reports and deliverables to the PRIMA management authorities.', duration: 'Months 1–36', leadPartner: 'ERU (Türkiye)', contributingPartners: 'All partners', completed: false }
      ],
      objectives: [
        'Ensure smooth coordination, financial management, and reporting throughout the duration of the MedAgriCycle project',
        'Maintain effective communication between all partners',
        'Manage administrative tasks efficiently',
        'Ensure the project stays on track according to timelines and budget'
      ]
    },
    {
      id: 'Wp1',
      title: 'Desk Analysis and Living Labs',
      description: 'WP1 aims to initiate the project by conducting comprehensive desk analysis in partner countries and establishing Living Labs (LLs) across strategically selected regions. These efforts will gather essential data and insights into urban and peri-urban agriculture markets while fostering collaboration and knowledge exchange among local farmers, policymakers, researchers, and stakeholders.',
      leader: 'ERU (Türkiye)',
      duration: 'Months 1–36',
      icon: '🔍',
      color: '#3498db',
      gradient: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
      date: '2024-01-01',
      progress: 70,
      status: 'active',
      contributingPartners: [
        { name: 'ERU (Türkiye)', personMonths: 10 },
        { name: 'TÜBİTAK MRC (Türkiye)', personMonths: 2.1 },
        { name: 'METU (Türkiye)', personMonths: 2.1 },
        { name: 'USMS (Morocco)', personMonths: 1.2 },
        { name: 'ENAM (Morocco)', personMonths: 1.2 },
        { name: 'JAPSS (France)', personMonths: 0.5 },
        { name: 'URCA (France)', personMonths: 0.5 },
        { name: 'INRGREF (Tunisia)', personMonths: 1.2 },
        { name: 'SRTA-City (Egypt)', personMonths: 1.2 },
        { name: 'UNICT (Italy)', personMonths: 1.2 }
      ],
      tasks: [
        { 
          id: 'T1.1', 
          title: 'Comprehensive Desk Analysis', 
          description: 'Each partner country will undertake an in-depth desk analysis to map the current state of urban and peri-urban agriculture in their respective regions, focusing on data collection, market analysis, and policy review.', 
          duration: 'Months 1–12', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'All partners',
          completed: true 
        },
        { 
          id: 'T1.2', 
          title: 'Establishment of Living Labs (LLs)', 
          description: 'Four Living Labs will be established across Türkiye, France, Morocco, Egypt, and Tunisia as interactive spaces for knowledge exchange and practical demonstrations of sustainable urban agriculture practices.', 
          duration: 'Months 1–18', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'All partners',
          completed: true 
        },
        { 
          id: 'T1.3', 
          title: 'Multi-Stakeholder Collaboration and Dissemination', 
          description: 'Foster long-term collaboration among project partners and stakeholders while disseminating findings and best practices through annual forums, cross-border knowledge sharing, and policy advocacy.', 
          duration: 'Months 12–36', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'All partners',
          completed: false 
        },
        { 
          id: 'T1.4', 
          title: 'Evaluation and Impact Assessment', 
          description: 'Evaluate the effectiveness of the desk analysis and Living Labs in generating actionable insights and promoting sustainable practices in urban agriculture through monitoring frameworks and impact analysis.', 
          duration: 'Months 24–36', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'All partners',
          completed: false 
        }
      ],
      objectives: [
        'Conduct in-depth desk analysis of urban agriculture practices in the Mediterranean region',
        'Implement Living Labs (LL) to validate innovations and collect stakeholder feedback for further refinements',
        'Foster collaboration and knowledge exchange among local farmers, policymakers, researchers, and stakeholders',
        'Evaluate and assess impact of activities on local food systems and environmental sustainability'
      ]
    },
    {
      id: 'Wp2',
      title: 'Developing a Digital Toolbox for the Management of Urban Agriculture',
      description: 'WP2 will create a comprehensive digital infrastructure that integrates technology, data, and collaboration to optimize urban agricultural practices, promoting sustainability across the Mediterranean region. By developing user-friendly tools and fostering cross-border collaboration, this work package will empower stakeholders to manage urban agriculture efficiently, enhance environmental outcomes, and drive innovation in sustainable food systems.',
      leader: 'JASSP SAS (France), URCA (France)',
      duration: 'Months 1–36',
      icon: '💻',
      color: '#f39c12',
      gradient: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
      date: '2024-01-01',
      progress: 45,
      status: 'active',
      contributingPartners: 'All partners (data collection from each partner)',
      tasks: [
        { 
          id: 'T2.1', 
          title: 'Metaverse/Digital Twin Information System Platform', 
          description: 'Develop an innovative Metaverse/Digital Twin platform leveraging IoT sensors to create a virtual model of urban agricultural environments with real-time data collection and decision support dashboard.', 
          duration: 'Months 1–18', 
          leadPartner: 'JASSP SAS (France)',
          contributingPartners: 'All partners',
          completed: true 
        },
        { 
          id: 'T2.2', 
          title: 'Development of a Digital Toolbox for the Circular Economy, Traceability, and Quality', 
          description: 'Create a comprehensive digital toolbox including mobile applications and website to manage urban agricultural practices, ensuring traceability and quality control with circular economy features.', 
          duration: 'Months 6–24', 
          leadPartner: 'URCA (France)',
          contributingPartners: 'All partners',
          completed: false 
        },
        { 
          id: 'T2.3', 
          title: 'E-Living Lab Platform for Mediterranean Collaboration', 
          description: 'Develop a digital E-Living Lab platform to foster collaboration and knowledge exchange across the Mediterranean region, connecting stakeholders and providing capacity building opportunities.', 
          duration: 'Months 6–30', 
          leadPartner: 'JASSP SAS (France)',
          contributingPartners: 'All partners',
          completed: false 
        },
        { 
          id: 'T2.4', 
          title: 'Integration of Desk Analysis, Trial Results, and Recommendations', 
          description: 'Integrate findings from desk analysis, field trials, and partner recommendations to inform the digital toolbox and E-Living Lab platform with actionable insights for end-users.', 
          duration: 'Months 18–36', 
          leadPartner: 'URCA (France)',
          contributingPartners: 'All partners',
          completed: false 
        },
        { 
          id: 'T2.5', 
          title: 'Development and Continuous Update of a User-Friendly Website', 
          description: 'Develop and continuously update a user-friendly website providing information, tools, and resources for urban agriculture practitioners with regular content updates and intuitive navigation.', 
          duration: 'Months 1–36', 
          leadPartner: 'JASSP SAS (France)',
          contributingPartners: 'All partners',
          completed: false 
        }
      ],
      objectives: [
        'Develop a comprehensive digital toolbox for urban agricultural waste management, optimized for use in Mediterranean urban settings',
        'Integrate IoT sensors, real-time monitoring, and data analysis to support sustainable urban agriculture practices',
        'Create a decision support system to simulate urban agriculture\'s impacts on urban microclimates'
      ],
    },
    {
      id: 'Wp3',
      title: 'Reusing Irrigation Water and Effective Irrigation',
      description: 'Enhance water use efficiency in agriculture through innovative irrigation techniques and reuse treated wastewater for irrigation in urban agriculture to reduce freshwater consumption.',
      leader: 'Erciyes University (ERU), Türkiye',
      duration: 'Months 1–36',
      icon: '💧',
      color: '#27ae60',
      gradient: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
      date: '2024-01-01',
      progress: 25,
      status: 'active',
      contributingPartners: 'University of Catania (Italy), SRTA-City (Egypt)',
      tasks: [
        { 
          id: 'T3.1', 
          title: 'Experimental Design and Irrigation Treatments (Türkiye)', 
          description: 'Establish experimental designs for faba bean, bean, and seed pumpkin trials using salinity-tolerant varieties with four water sources: WWTP effluent, pilot-scale MBR and NF systems, drainage water, and fresh groundwater.', 
          duration: 'Months 1–18', 
          leadPartner: 'Erciyes University (ERU), Türkiye',
          contributingPartners: 'UNICT (Italy), SRTA-City (Egypt)',
          completed: false 
        },
        { 
          id: 'T3.2', 
          title: 'Monitoring of Contaminants and Crop Responses (Türkiye)', 
          description: 'Monitor emerging contaminants including micropollutants, heavy metals, and other risky pollutants, evaluating their accumulation in soil and plants and effects on crop yields and quality.', 
          duration: 'Months 6–24', 
          leadPartner: 'Erciyes University (ERU), Türkiye',
          contributingPartners: 'UNICT (Italy), SRTA-City (Egypt)',
          completed: false 
        },
        { 
          id: 'T3.3', 
          title: 'Physiological and Morphological Monitoring (Italy)', 
          description: 'Evaluate almond rootstock and cultivar combinations under different irrigation regimes with target 25% and 50% water deficit conditions, including morphological and physiological measurements.', 
          duration: 'Months 6–30', 
          leadPartner: 'University of Catania (Italy)',
          contributingPartners: 'ERU (Türkiye), SRTA-City (Egypt)',
          completed: false 
        },
        { 
          id: 'T3.4', 
          title: 'Field Trials and Case Studies (Egypt)', 
          description: 'Conduct case studies focusing on water efficiency in olive and Opuntia cultivation, evaluating conventional irrigation and innovative drip systems with drought-resistant Opuntia plants.', 
          duration: 'Months 6–36', 
          leadPartner: 'SRTA-City, Egypt',
          contributingPartners: 'ERU (Türkiye), SRTA-City (Egypt)',
          completed: false 
        },
        { 
          id: 'T3.5', 
          title: 'Plant Growth and Soil Analysis (Türkiye)', 
          description: 'Evaluate vegetative growth, seed yield, and soil health parameters across all crops, monitoring flowering time, fruit yield, and assessing soil salinity, pH levels, and heavy metal accumulation.', 
          duration: 'Months 12–36', 
          leadPartner: 'Erciyes University (ERU), Türkiye',
          contributingPartners: 'UNICT (Italy), SRTA-City (Egypt)',
          completed: false 
        }
      ],
      objectives: [
        'Enhance water use efficiency through innovative irrigation techniques',
        'Reuse treated wastewater for irrigation in urban agriculture',
        'Analyze effects of different irrigation regimes on plant growth, yield, and water efficiency',
        'Assess physiological and morphological responses of almond rootstocks under water stress conditions'
      ],
    },
    {
      id: 'Wp4',
      title: 'Composting from Agricultural and Urban Waste and Determining Its Effects',
      description: 'The main goal of WP4 is to produce high-quality compost from agricultural residues, urban food waste, and municipal waste to enhance soil quality and agricultural productivity. The work package will assess environmental and socio-economic impacts of these composting processes in Mediterranean regions, promoting sustainable waste management practices.',
      leader: 'USMS (Morocco)',
      duration: 'Months 1–36',
      icon: '♻️',
      color: '#9b59b6',
      gradient: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
      date: '2024-01-01',
      progress: 15,
      status: 'active',
      contributingPartners: 'Erciyes University (Türkiye), INRGREF (Tunisia)',
      tasks: [
        { 
          id: 'T4.1', 
          title: 'Compost Production Using the Berkeley Rapid Composting Method (Türkiye)', 
          description: 'Prepare compost using crop residues from pumpkin, fava bean, and bean along with food waste from university campus and selected municipal waste, with regular turning and monitoring.', 
          duration: 'Months 1-12', 
          leadPartner: 'Erciyes University (ERU), Türkiye',
          contributingPartners: 'USMS (Morocco), INRGREF (Tunisia)',
          completed: false 
        },
        { 
          id: 'T4.2', 
          title: 'Determination of Compost Chemical and Physical Properties (Türkiye)', 
          description: 'Monitor compost for temperature, pH, electrical conductivity, and dry bulk density on specific turning days, with measurements at five different points within the pile.', 
          duration: 'Months 13-24', 
          leadPartner: 'Erciyes University (ERU), Türkiye',
          contributingPartners: 'USMS (Morocco), INRGREF (Tunisia)',
          completed: false 
        },
        { 
          id: 'T4.3', 
          title: 'Urban and Peri-urban Agriculture Assessment (Morocco)', 
          description: 'Assess urban and peri-urban agricultural models in Morocco, focusing on soil and water quality, crop management, and socio-economic impact through surveys in Moulay Driss Zerhoun and Meknes.', 
          duration: 'Months 1-24', 
          leadPartner: 'USMS (Morocco)',
          contributingPartners: 'ERU (Türkiye), INRGREF (Tunisia)',
          completed: false 
        },
        { 
          id: 'T4.4', 
          title: 'Field Trials and Compost Application Impact on Soil and Crop Productivity (Morocco)', 
          description: 'Conduct field trials to assess the effects of compost on soil nutrient dynamics, crop yield, and nutrient leaching risk, testing organic biofertilizers to enhance crop yields.', 
          duration: 'Months 13-36', 
          leadPartner: 'USMS (Morocco)',
          contributingPartners: 'ERU (Türkiye), INRGREF (Tunisia)',
          completed: false 
        },
        { 
          id: 'T4.5', 
          title: 'Distillation Waste Valorization and Development of Organic Amendments (Tunisia)', 
          description: 'Conduct comprehensive analysis of chemical composition and nutrient content of distillation by-products, developing protocols to process waste into valuable soil amendments.', 
          duration: 'Months 1-36', 
          leadPartner: 'INRGREF (Tunisia)',
          contributingPartners: 'USMS (Morocco), ERU (Türkiye)',
          completed: false 
        },
        { 
          id: 'T4.6', 
          title: 'Field Trials and Integration of Organic Amendments into Urban Agriculture (Tunisia)', 
          description: 'Conduct field trials to integrate compost and organic amendments into urban farming practices, measuring soil health, crop yield, and moisture retention.', 
          duration: 'Months 25-36', 
          leadPartner: 'INRGREF (Tunisia)',
          contributingPartners: 'USMS (Morocco), ERU (Türkiye)',
          completed: false 
        },
        { 
          id: 'T4.7', 
          title: 'Socio-economic Impact Assessment (Morocco, Tunisia, Türkiye)', 
          description: 'Assess socio-economic benefits of composting and organic waste valorization in urban agriculture through surveys and interviews with farmers and stakeholders.', 
          duration: 'Months 25-36', 
          leadPartner: 'USMS (Morocco)',
          contributingPartners: 'INRGREF (Tunisia), ERU (Türkiye)',
          completed: false 
        }
      ],
      objectives: [
        'Produce high-quality compost from agricultural and urban waste',
        'Assess environmental and socio-economic impacts of composting',
        'Promote sustainable waste management practices',
        'Enhance soil quality and agricultural productivity'
      ],
    },
    {
      id: 'Wp5',
      title: 'Extraction of High-Value Plant Proteins from Agricultural Waste',
      description: 'WP5 aims to harness agricultural waste (leaves) from common beans, faba beans, and pumpkins as a source of sustainable plant proteins, extracted and characterized using conventional, spectroscopic, and mass spectrometric techniques.',
      leader: 'METU (Türkiye)',
      duration: 'Months 6–36',
      icon: '🧬',
      color: '#34495e',
      gradient: 'linear-gradient(135deg, #34495e 0%, #2c3e50 100%)',
      date: '2024-06-01',
      progress: 5,
      status: 'planning',
      contributingPartners: 'ERU (Türkiye)',
      tasks: [
        { 
          id: 'T5.1', 
          title: 'Protein Quantification by Conventional Methods (Türkiye)', 
          description: 'Determine protein content of common beans, faba beans, and pumpkins using Kjeldahl and Dumas methods to assess nitrogen content as indirect measure of protein.', 
          duration: 'Months 6–12', 
          leadPartner: 'METU (Türkiye)',
          contributingPartners: 'ERU (Türkiye)',
          completed: false 
        },
        { 
          id: 'T5.2', 
          title: 'Protein Characterization by Mass Spectrometry (Türkiye)', 
          description: 'Perform protein extraction using Universal Extraction Kit, spectroscopic determination, FASP digestion method, and LC-MS/MS analysis for comprehensive characterization.', 
          duration: 'Months 12–24', 
          leadPartner: 'METU (Türkiye)',
          contributingPartners: 'ERU (Türkiye)',
          completed: false 
        },
        { 
          id: 'T5.3', 
          title: 'Protein Profiling and Statistical Analysis (Türkiye)', 
          description: 'Use Proteome Discoverer 2.5 software with Sequest HT algorithm for proteome analysis, performing label-free quantification and statistical analysis of protein expression levels.', 
          duration: 'Months 18–30', 
          leadPartner: 'METU (Türkiye)',
          contributingPartners: 'ERU (Türkiye)',
          completed: false 
        },
        { 
          id: 'T5.4', 
          title: 'Bioinformatic Analysis for Protein Allergen Risk Prediction (Türkiye)', 
          description: 'Apply bioinformatic tools to protein lists from mass spectrometry analysis for allergen risk prediction, comparing identified proteins against known allergenic proteins.', 
          duration: 'Months 24–30', 
          leadPartner: 'METU (Türkiye)',
          contributingPartners: 'ERU (Türkiye)',
          completed: false 
        },
        { 
          id: 'T5.5', 
          title: 'Protein Extraction and Precipitation for Production (Türkiye)', 
          description: 'Homogenize leaf samples and perform protein extraction using alkali extraction, followed by isoelectric precipitation and freeze-drying to produce protein powders.', 
          duration: 'Months 30–36', 
          leadPartner: 'METU (Türkiye)',
          contributingPartners: 'ERU (Türkiye)',
          completed: false 
        }
      ],
      objectives: [
        'Extract and characterize high-value plant proteins from agricultural waste',
        'Quantify protein content using conventional and advanced methods',
        'Assess allergen risk through bioinformatic analysis',
        'Produce protein powders for food and agricultural applications'
      ],
    },
    {
      id: 'Wp6',
      title: 'Extraction of Economically Significant Food Additives from Agricultural Waste',
      description: 'The main goal of WP6 is to innovate in the extraction of valuable food additives from seed pumpkin agricultural waste, focusing on valorizing pumpkin by-products by extracting bioactive compounds for food industry applications.',
      leader: 'TÜBİTAK MRC (Türkiye)',
      duration: 'Months 6–36',
      icon: '🔬',
      color: '#e67e22',
      gradient: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
      date: '2024-06-01',
      progress: 5,
      status: 'planning',
      tasks: [
        { 
          id: 'T6.1', 
          title: 'Characterisation of Pumpkin By-products', 
          description: 'Conduct detailed chemical and physical characterization of pumpkin by-products, analyzing moisture, lipid, protein, starch, carotenoid, and phenolic content, fatty acid composition and mineral content.', 
          duration: 'Months 6–18', 
          leadPartner: 'TÜBİTAK MRC (Türkiye)',
          contributingPartners: 'N/A',
          completed: false 
        },
        { 
          id: 'T6.2', 
          title: 'Green Extraction of Bioactive Compounds', 
          description: 'Employ UAE and MAE techniques for extracting carotenoids and phenolic compounds using DES as green solvents, reducing environmental impact and enhancing extraction efficiency.', 
          duration: 'Months 18–24', 
          leadPartner: 'TÜBİTAK MRC (Türkiye)',
          contributingPartners: 'N/A',
          completed: false 
        },
        { 
          id: 'T6.3', 
          title: 'Selection of Green Extraction System for Pectin, Carotenoids, and Phenolic Compounds', 
          description: 'Screen and optimize various DES formulations for their capacity to extract pectin, carotenoids, and phenolic compounds, focusing on maximizing yield and bioactivity.', 
          duration: 'Months 18–30', 
          leadPartner: 'TÜBİTAK MRC (Türkiye)',
          contributingPartners: 'N/A',
          completed: false 
        },
        { 
          id: 'T6.4', 
          title: 'Extraction of Pectin, Carotenoids, and Phenolic Compounds from Pumpkin By-products', 
          description: 'Refine and scale extraction processes for pectin and bioactive compounds, using filtration and precipitation techniques to recover pectin with additional separation of phenolic compounds.', 
          duration: 'Months 24–36', 
          leadPartner: 'TÜBİTAK MRC (Türkiye)',
          contributingPartners: 'N/A',
          completed: false 
        },
        { 
          id: 'T6.5', 
          title: 'Formulation of the Bioactive Extracts with the Green Solvent System', 
          description: 'Formulate extracted compounds into functional food ingredients, conducting analysis to ensure product purity, bioactivity, and antioxidant properties.', 
          duration: 'Months 30–36', 
          leadPartner: 'TÜBİTAK MRC (Türkiye)',
          contributingPartners: 'N/A',
          completed: false 
        },
        { 
          id: 'T6.6', 
          title: 'Development of New Functional Foods and Dietary Supplements', 
          description: 'Develop and characterize functional food products and supplements enriched with extracted bioactive compounds, performing extensive physico-chemical, microbiological, and organoleptic analysis.', 
          duration: 'Months 30–36', 
          leadPartner: 'TÜBİTAK MRC (Türkiye)',
          contributingPartners: 'N/A',
          completed: false 
        }
      ],
      objectives: [
        'Characterize pumpkin by-products for valuable compounds',
        'Develop green extraction methods for bioactive compounds',
        'Extract pectin, carotenoids, and phenolic compounds',
        'Develop functional foods and dietary supplements'
      ],
    },
    {
      id: 'Wp7',
      title: 'Policy Recommendations for the Sustainability of Urban Agriculture and Its Environmental and Agricultural Impacts',
      description: 'This work package aims to deliver policy recommendations that promote sustainable urban agriculture while mitigating its environmental and agricultural impacts. The recommendations will focus on addressing climate change, carbon emissions, and sustainable development.',
      leader: 'ERU (Türkiye)',
      duration: 'Months 1–36',
      icon: '🏛️',
      color: '#8e44ad',
      gradient: 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)',
      date: '2024-01-01',
      progress: 5,
      status: 'planning',
      tasks: [
        { 
          id: 'T7.1', 
          title: 'Econometric and Statistical Analysis', 
          description: 'Conduct econometric and statistical analyses using data from previous work packages to quantify social and environmental impacts of urban agriculture, with contributions for regional-specific data.', 
          duration: 'Months 1–12', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'USMS (Morocco), INRGREF (Tunisia)',
          completed: false 
        },
        { 
          id: 'T7.2', 
          title: 'Multi-Criteria Decision-Making Analysis', 
          description: 'Employ multi-criteria decision-making methods integrated with intuitionistic fuzzy set theory to prioritize key criteria for policy formulation in urban agriculture, using IF-TOPSIS method.', 
          duration: 'Months 1–36', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'All partners',
          completed: false 
        }
      ],
      objectives: [
        'Conduct econometric and statistical analysis of urban agriculture impacts',
        'Apply multi-criteria decision-making methods with fuzzy set theory',
        'Develop policy recommendations for sustainable urban agriculture',
        'Address environmental and agricultural impacts through policy'
      ],
    },
    {
      id: 'Wp8',
      title: 'Dissemination, Exploitation, Communication, and Outreach (DECO) Strategy',
      description: 'The primary goal of WP8 is to ensure that the findings and innovations generated by MedAgriCycle are widely disseminated to relevant stakeholders. This WP will employ both digital platforms and face-to-face engagement strategies.',
      leader: 'ERU (Türkiye)',
      duration: 'Months 1–36',
      icon: '📡',
      color: '#16a085',
      gradient: 'linear-gradient(135deg, #16a085 0%, #1abc9c 100%)',
      date: '2024-01-01',
      progress: 5,
      status: 'planning',
      contributingPartners: 'All partners',
      tasks: [
        { 
          id: 'T8.1', 
          title: 'Development of a Comprehensive DECO Strategy', 
          description: 'Create detailed DECO plan outlining key objectives, target audiences, and communication tools, including stakeholder mapping and gender mainstreaming in communication.', 
          duration: 'Months 1–6', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'All partners',
          completed: false 
        },
        { 
          id: 'T8.2', 
          title: 'Creation and Maintenance of the Project Website and Digital Platforms', 
          description: 'Create project website as primary hub for public-facing materials, featuring E-Living Lab platform and mobile application optimized for real-time project data access.', 
          duration: 'Months 1–36', 
          leadPartner: 'JASSP SAS (France)',
          contributingPartners: 'URCA (France), All partners',
          completed: false 
        },
        { 
          id: 'T8.3', 
          title: 'Social Media and Digital Outreach Campaigns', 
          description: 'Create official project accounts on social media platforms, with regular updates on project progress, events, and key findings tailored for different audiences.', 
          duration: 'Months 6–36', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'All partners',
          completed: false 
        },
        { 
          id: 'T8.4', 
          title: 'Public Presentations, Press Releases, and Media Engagement', 
          description: 'Present at conferences and workshops, issue press releases highlighting key milestones, and establish partnerships with media outlets for regular coverage.', 
          duration: 'Months 6–36', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'All partners',
          completed: false 
        },
        { 
          id: 'T8.5', 
          title: 'Workshops and Training Sessions for Stakeholders', 
          description: 'Develop interactive workshops tailored to different stakeholder groups, with regular training sessions held both virtually and in person, ensuring gender-inclusive participation.', 
          duration: 'Months 12–36', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'All partners',
          completed: false 
        },
        { 
          id: 'T8.6', 
          title: 'Dissemination of Scientific Publications and Policy Briefs', 
          description: 'Submit research results to peer-reviewed journals, develop policy briefs for decision-makers, and collaborate with other EU-funded projects to maximize impact.', 
          duration: 'Months 12–36', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'URCA (France), All partners',
          completed: false 
        },
        { 
          id: 'T8.7', 
          title: 'Gender Inclusion and Diversity in Dissemination', 
          description: 'Ensure all communication and outreach efforts promote gender equality, highlight women scientists in key project roles, and provide gender-sensitive training and participation.', 
          duration: 'Months 1–36', 
          leadPartner: 'ERU (Türkiye)',
          contributingPartners: 'All partners',
          completed: false 
        }
      ],
      objectives: [
        'Develop comprehensive DECO strategy',
        'Create and maintain project website and digital platforms',
        'Conduct social media and digital outreach campaigns',
        'Organize workshops and training sessions for stakeholders'
      ],
    }
  ];

  const filteredWorkPackages = workPackages;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long'
    });
  };

  const toggleWP = (wpId) => {
    setExpandedWP(expandedWP === wpId ? null : wpId);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <div class="home page-template page-template-page-templates page-template-fullwidth-content page-template-page-templatesfullwidth-content-php page page-id-4059 jet-theme-core jet-theme-core--has-template jet-theme-core--has-header jet-theme-core--has-body jet-theme-core--has-footer layout-fullwidth blog-masonry elementor-default elementor-kit-5747 elementor-page elementor-page-4059">
        <div id="jet-theme-core-document" class="jet-theme-core-document jet-theme-core-document--elementor-content-type"><div class="jet-theme-core-document__inner">
	
	<Navbar/>
	
	{/* Composant de test des icônes FontAwesome */}
	
	<article id="post-4059" class="post-4059 page type-page status-publish hentry">	
			<div data-elementor-type="wp-page" data-elementor-id="4059" class="elementor elementor-4059">
				<section class="elementor-section elementor-top-section elementor-element elementor-element-ic2p76x elementor-section-full_width elementor-section-height-default elementor-section-height-default" data-id="ic2p76x" data-element_type="section" data-settings="{&quot;jet_parallax_layout_list&quot;:[{&quot;jet_parallax_layout_image&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;_id&quot;:&quot;5759a04&quot;,&quot;jet_parallax_layout_image_tablet&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;jet_parallax_layout_image_mobile&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;jet_parallax_layout_speed&quot;:{&quot;unit&quot;:&quot;%&quot;,&quot;size&quot;:50,&quot;sizes&quot;:[]},&quot;jet_parallax_layout_type&quot;:&quot;scroll&quot;,&quot;jet_parallax_layout_direction&quot;:null,&quot;jet_parallax_layout_fx_direction&quot;:null,&quot;jet_parallax_layout_z_index&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_x&quot;:50,&quot;jet_parallax_layout_bg_x_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_x_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_y&quot;:50,&quot;jet_parallax_layout_bg_y_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_y_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_size&quot;:&quot;auto&quot;,&quot;jet_parallax_layout_bg_size_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_size_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_animation_prop&quot;:&quot;transform&quot;,&quot;jet_parallax_layout_on&quot;:[&quot;desktop&quot;,&quot;tablet&quot;]}]}">
					<div class="elementor-container elementor-column-gap-default">
						<div class="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-im9c73l" data-id="im9c73l" data-element_type="column">
							<div class="elementor-widget-wrap elementor-element-populated">
								<div class="elementor-element elementor-element-0bd4fc3 elementor-widget elementor-widget-jet-slider" data-id="0bd4fc3" data-element_type="widget" data-settings="{&quot;slider_height&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:852,&quot;sizes&quot;:[]},&quot;_animation&quot;:&quot;none&quot;,&quot;slider_height_tablet&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:650,&quot;sizes&quot;:[]},&quot;slider_height_mobile&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:500,&quot;sizes&quot;:[]}}" data-widget_type="jet-slider.default">
									<div class="elementor-widget-container">
										<div class="elementor-jet-slider jet-elements">
                        <div style={{ 
                          position: 'relative', 
                          width: '100%', 
                          height: isMobile ? 'auto' : '500px', 
                          minHeight: isMobile ? '400px' : '500px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: 'center',
                          padding: isMobile ? '100px 20px 40px' : '0',
                          background: 'linear-gradient(135deg, rgba(46,134,171,0.8) 0%, rgba(25,118,157,0.8) 50%, rgba(40,167,69,0.8) 100%), url("https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&h=1080&fit=crop") center/cover no-repeat'
                        }}>
                          {/* Left Side - Text Content */}
                          <div style={{ 
                            flex: '1', 
                            padding: isMobile ? '0' : '0 100px',
                            width: '100%'
                          }}>
                            <div style={{
                              maxWidth: isMobile ? '100%' : '800px'
                            }}>
                              <h1 style={{ 
                                color: '#fff', 
                                fontSize: isMobile ? 'clamp(28px,3vw,42px)' : 'clamp(32px,4.5vw,54px)', 
                                fontWeight: '800', 
                                margin: '0 0 20px 0', 
                                letterSpacing: '-0.02em',
                                lineHeight: '1.1',
                                textAlign: isMobile ? 'center' : 'left'
                              }}>
                                {t('workPackages.title')}
                              </h1>
                              <p style={{ 
                                color: 'rgba(255,255,255,0.95)', 
                                fontSize: isMobile ? 'clamp(14px,2vw,18px)' : 'clamp(16px,2vw,20px)', 
                                lineHeight: 1.6, 
                                margin: '0 0 32px 0',
                                fontWeight: '400',
                                textAlign: isMobile ? 'center' : 'left'
                              }}>
                                {t('workPackages.subtitle')}
                              </p>
                          </div>
                        </div>

                          {/* Right Side - Animated Work Packages */}
                        <div style={{
                          display: 'flex',
                          flex: '0 0 auto',
                          width: isMobile ? '100%' : '500px',
                          padding: isMobile ? '20px 0' : '0 40px',
                          marginRight: isMobile ? '0' : '80px',
                          marginTop: isMobile ? '20px' : '0',
                          position: 'relative'
                        }}>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '16px',
                              height: isMobile ? '200px' : '276px',
                              overflow: 'hidden',
                              position: 'relative',
                              background: 'linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
                              borderRadius: '16px',
                              border: '1px solid rgba(255,255,255,0.2)',
                              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
                              width: '100%'
                            }}>
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                animation: 'elevatorMoveUp 36s linear infinite'
                              }}>
                              
                              {/* WorkPackages en boucle continue - triplé pour défilement très fluide */}
                              {[...workPackages, ...workPackages, ...workPackages].map((wp, index) => (
                                <div
                                  key={wp.id}
                                  onClick={() => toggleWP(wp.id)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '20px 24px',
                                    background: expandedWP === wp.id ? 
                                      `linear-gradient(45deg, ${wp.color}25, rgba(255, 255, 255, 0.15), ${wp.color}25)` : 
                                      'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '8px',
                                    margin: '0',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    height: '100px',
                                    flexShrink: 0,
                                    position: 'relative',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    border: `1px solid ${expandedWP === wp.id ? wp.color : 'rgba(255, 255, 255, 0.2)'}`,
                                    transform: expandedWP === wp.id ? 'scale(1.02)' : 'scale(1)',
                                    boxShadow: expandedWP === wp.id ? `0 6px 24px ${wp.color}40` : '0 2px 8px rgba(0, 0, 0, 0.1), inset 0 0 0 1px rgba(255,255,255,0.1)',
                                    animationDelay: `${index * 2}s`,
                                  
                                    backgroundSize: expandedWP === wp.id ? '200% 200%' : '100% 100%',
                                    backgroundPosition: expandedWP === wp.id ? '-200% 0' : '0 0'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (expandedWP !== wp.id) {
                                      e.currentTarget.style.background = `linear-gradient(45deg, ${wp.color}30, rgba(255, 255, 255, 0.2), ${wp.color}30)`;
                                      e.currentTarget.style.borderColor = `${wp.color}60`;
                                      e.currentTarget.style.transform = 'scale(1.02) translateY(-2px)';
                                      e.currentTarget.style.boxShadow = `0 8px 30px ${wp.color}40`;
                                      e.currentTarget.style.animation = `pulse 1s ease-in-out infinite`;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (expandedWP !== wp.id) {
                                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                      e.currentTarget.style.transform = 'scale(1) translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                                      e.currentTarget.style.animation = 'none';
                                    }
                                  }}
                                >
                                  {/* Icon */}
                                  <div 
                                    className={`wp-icon-${index}`}
                                    style={{
                                      width: '50px',
                                      height: '50px',
                                      borderRadius: '8px',
                                      background: expandedWP === wp.id ? 'rgba(255, 255, 255, 0.3)' : `${wp.color}30`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '1.5rem',
                                      color: expandedWP === wp.id ? 'white' : 'white',
                                      flexShrink: 0,
                                      border: `1px solid ${expandedWP === wp.id ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.3)'}`,
                                      transition: 'all 0.3s ease',
                                      animation: expandedWP === wp.id ? 
                                        `bounce 1s ease-in-out infinite` : 
                                        'none',
                                      animationDelay: '0s'
                                    }}
                                  >
                                    {wp.icon}
                      </div>
                                  
                                  {/* WP Info */}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                      fontSize: '1.2rem',
                                      fontWeight: '700',
                                      color: '#fff',
                                      lineHeight: '1.2',
                                      marginBottom: '4px',
                                      letterSpacing: '0.025em'
                                    }}>
                                      {wp.id}
                    </div>
                                    <div style={{
                                      fontSize: '1.0rem',
                                      color: expandedWP === wp.id ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.85)',
                                      lineHeight: '1.3',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {wp.title.split(' ').slice(0, 5).join(' ')}
                                      {wp.title.split(' ').length > 5 && '...'}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
</div>
                    </article>

          {/* CSS Animations */}
          <style jsx>{`
            @keyframes elevatorMoveUp {
              0% {
                transform: translateY(0);
              }
              33.33% {
                transform: translateY(-33.33%);
              }
              66.66% {
                transform: translateY(-66.66%);
              }
              100% {
                transform: translateY(-33.33%);
              }
            }

            @keyframes centerZoom {
              0%, 3% {
                transform: scale(1);
                z-index: 1;
              }
              8%, 12% {
                transform: scale(1.1);
                z-index: 10;
                box-shadow: 0 8px 32px rgba(0,0,0,0.3);
              }
              17%, 100% {
                transform: scale(1);
                z-index: 1;
              }
            }
            
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes pulse {
              0% {
                transform: scale(1);
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
              }
              50% {
                transform: scale(1.05);
                box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
              }
              100% {
                transform: scale(1);
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
              }
            }

            @keyframes shimmer {
              0% {
                background-position: -200% 0;
              }
              100% {
                background-position: 200% 0;
              }
            }

            @keyframes float {
              0%, 100% {
                transform: translateY(0) rotate(0deg);
              }
              25% {
                transform: translateY(-5px) rotate(1deg);
              }
              75% {
                transform: translateY(-8px) rotate(-1deg);
              }
            }

            @keyframes bounce {
              0%, 20%, 50%, 80%, 100% {
                transform: translateY(0);
              }
              40% {
                transform: translateY(-10px);
              }
              60% {
                transform: translateY(-5px);
              }
            }

          `}</style>

        {/* Work Packages Section */}
        <section className="workpackages-content" style={{
          padding: '40px 0',
          background: '#f8f9fa',
          minHeight: '100vh'
        }}>
            <div className="container" style={{ 
              maxWidth: '1200px',
              margin: '0 auto',
              padding: isMobile ? '16px' : '0 20px'
            }}>
            <main>
            {isMobile && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '17px',
                marginBottom: '30px',
                justifyContent: 'center'
              }}>
                {filteredWorkPackages.map((wp) => (
                  <button
                    key={wp.id}
                    onClick={() => toggleWP(wp.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      background: expandedWP === wp.id ? '#96ca2d' : '#f8f9fa',
                      color: expandedWP === wp.id ? '#ffffff' : '#495057',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}
                    onMouseEnter={(e) => {
                      if (expandedWP !== wp.id) {
                        e.currentTarget.style.background = '#e9ecef';
                        e.currentTarget.style.borderColor = '#96ca2d';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (expandedWP !== wp.id) {
                        e.currentTarget.style.background = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#e9ecef';
                      }
                    }}
                  >
                    <span>{wp.icon}</span>
                    <span>{wp.id}</span>
                  </button>
                ))}
              </div>)}

              {/* Accordion Layout */}
              <div className="accordion-layout" style={{
                display: 'flex',
                gap: '30px',
                minHeight: '600px',
                flexDirection: 'row'
              }}>
                {/* Sidebar Navigation */}
                <div className="sidebar-nav" style={{
                  width: isMobile ? '100%' : '320px',
                  background: 'white',
                  borderRadius: '12px',
                  padding: '25px',
                  height: 'fit-content',
                  position: isMobile ? 'static' : 'sticky',
                  top: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef',
                  display: isMobile ? 'none' : 'block'
                }}>
                  <div style={{
                    margin: '0 0 20px 0',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    color: '#2c3e50'
                  }}>
                    {t('workPackages.sidebarTitle')}
                  </div>
              <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {filteredWorkPackages.map((wp, index) => (
                      <button
                        key={wp.id}
                        onClick={() => toggleWP(wp.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          padding: '12px 16px',
                          background: expandedWP === wp.id ? '#96ca2d' : 'transparent',
                          color: expandedWP === wp.id ? 'white' : '#495057',
                    border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          textAlign: 'left',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          width: '100%',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word'
                  }}
                  onMouseEnter={(e) => {
                          if (expandedWP !== wp.id) {
                            e.currentTarget.style.background = '#e9ecef';
                          }
                  }}
                  onMouseLeave={(e) => {
                          if (expandedWP !== wp.id) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                      >
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          background: expandedWP === wp.id ? '#96ca2d' : '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1rem',
                          color: expandedWP === wp.id ? 'white' : '#6c757d',
                          flexShrink: 0,
                          border: '1px solid #e9ecef'
                        }}>
                          {wp.icon}
                        </div>
                        <div style={{ 
                          flex: 1,
                          minWidth: 0,
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          <div style={{
                            fontWeight: '600',
                            marginBottom: '2px',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {wp.id}
                          </div>
                          <div style={{
                            fontSize: '1rem',
                            opacity: 0.8,
                            lineHeight: 1.3,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                            textTransform: 'none'

                          }}>
                            {wp.title}
                          </div>
                        </div>
                      </button>
                    ))}
                      </div>
                    </div>

                {/* Main Content */}
                <div className="main-content" style={{
                  flex: 1,
                  minHeight: '600px',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflowX: 'hidden',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}>
                  {expandedWP ? (
                    filteredWorkPackages.find(wp => wp.id === expandedWP) && (
                      <article className="workpackage-detail" style={{ 
                        background: '#ffffff',
                        borderRadius: '12px',
                        padding: isMobile ? '15px' : '40px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #e9ecef',
                        animation: 'fadeIn 0.3s ease-in-out',
                        maxWidth: '100%',
                        width: '100%',
                        overflow: 'visible',
                        overflowX: 'hidden',
                        wordWrap: 'break-word',
                        wordBreak: 'break-word',
                        boxSizing: 'border-box'
                      }}>
                        {(() => {
                          const wp = filteredWorkPackages.find(wp => wp.id === expandedWP);
                          // Récupérer les deliverables pour ce work package depuis la base de données
                          const wpDeliverables = wp ? deliverables.filter(d => d.wp === wp.id) : [];
                          return (
                            <>
                              {/* Header */}
                              <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                marginBottom: '30px',
                                paddingBottom: '20px',
                                borderBottom: '2px solid #f8f9fa',
                                flexDirection: isMobile ? 'column' : 'row'
                              }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    marginBottom: '12px'
                                  }}>
                    <div style={{ 
                                      width: isMobile ? '50px' : '60px',
                                      height: isMobile ? '50px' : '60px',
                                      borderRadius: '12px',
                                      background: '#f8f9fa',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: isMobile ? '1.5rem' : '2rem',
                                      color: '#6c757d',
                                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                      border: '1px solid #e9ecef'
                                    }}>
                                      {wp.icon}
                                    </div>
                      <div>
                                      <h2 style={{ 
                                        margin: 0, 
                                        fontSize: isMobile ? '1.3rem' : '1.8rem', 
                                        fontWeight: '700', 
                                        color: '#2c3e50',
                                        lineHeight: 1.2,
                                        textTransform: 'none',
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                        overflow: 'visible'
                                      }}>
                                        {wp.title}
                                      </h2>
                                      <div style={{
                                        fontSize: '1rem',
                                        color: '#6c757d',
                                        marginTop: '4px',
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word'
                                      }}>
                                        {wp.id} • {wp.duration}
                                      </div>
                                      <div style={{
                                        fontSize: '0.9rem',
                                        color: '#6c757d',
                                        marginTop: '8px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '6px',
                                        overflow: 'visible',
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word'
                                      }}>
                                        <i className="fas fa-user" style={{ color: '#96ca2d' }}></i>
                                        {t('workPackages.labels.leadPartner')}: {wp.leader}
                                      </div>
                                      {wp.contributingPartners && (
                                        <div style={{
                                          fontSize: '0.9rem',
                                          color: '#6c757d',
                                          marginTop: '8px',
                          display: 'flex',
                          alignItems: 'flex-start',
                                        gap: '6px',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        overflow: 'visible'
                                        }}>
                                          <i className="fas fa-users" style={{ color: '#96ca2d', flexShrink: 0, marginTop: '2px' }}></i>
                                          <div style={{ 
                                            flex: 1, 
                                            minWidth: 0,
                                            wordWrap: 'break-word',
                                            overflowWrap: 'break-word',
                                            lineHeight: '1.4',
                                            overflow: 'visible'
                                          }}>
                                            {t('workPackages.labels.contributingPartners')}: {Array.isArray(wp.contributingPartners) ? (
                                              wp.contributingPartners.map((partner, index) => (
                                                <span key={index}>
                                                  {partner.name}
                                                  {index < wp.contributingPartners.length - 1 ? ', ' : ''}
                                                </span>
                                              ))
                                            ) : (
                                              wp.contributingPartners
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Objectives Section */}
                              <div style={{ marginBottom: '30px' }}>
                                <h3 style={{
                                  fontSize: isMobile ? '1rem' : '1.3rem',
                                  fontWeight: '600',
                                  color: '#2c3e50',
                          marginBottom: '20px',
                          display: 'flex',
                          alignItems: 'center',
                                  gap: '12px'
                                }}>
                                  <i className="fas fa-bullseye" style={{ fontSize: isMobile ? '1rem' : '1.2rem', color: '#96ca2d' }}></i>
                                  {t('workPackages.labels.objectives')}
                                </h3>
                                <div style={{
                                  display: 'grid',
                                  gap: '15px'
                                }}>
                                  {wp.objectives.map((objective, index) => (
                                    <div key={index} style={{
                                      background: 'white',
                                      padding: isMobile ? '15px' : '20px',
                                      borderRadius: '12px',
                                      border: '2px solid #e9ecef',
                                      boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)';
                            }}
                            onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                                    }}>
                                      <div style={{ flex: 1 }}>
                                        <h4 style={{
                                          margin: '0 0 8px 0',
                                          fontSize: isMobile ? '1rem' : '1.1rem',
                                          fontWeight: '600',
                                          color: '#2c3e50',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '10px'
                            }}>
                              <span style={{
                                              background: '#f8f9fa',
                                              color: '#6c757d',
                                              padding: '4px 8px',
                                              borderRadius: '6px',
                                              fontSize: '0.7rem',
                                              fontWeight: '600',
                                              border: '1px solid #e9ecef'
                                            }}>
                                              {t('workPackages.labels.objective')} {index + 1}
                                            </span>
                                        </h4>
                                        <p style={{
                                          color: '#495057',
                                          fontSize: '0.95rem',
                                          lineHeight: 1.5,
                                          margin: 0,
                                          wordWrap: 'break-word',
                                          overflowWrap: 'break-word',
                                          overflow: 'visible'
                                        }}>
                                          {objective}
                                        </p>
                    </div>
                  </div>
                ))}
                                </div>
              </div>

                              {/* Tasks Section */}
                              <div style={{ marginBottom: '30px' }}>
                                <h3 style={{
                                  fontSize: isMobile ? '1rem' : '1.3rem',
                                  fontWeight: '600',
                                  color: '#2c3e50',
                          marginBottom: '20px',
                          display: 'flex',
                          alignItems: 'center',
                                  gap: '12px'
                                }}>
                                  <i className="fas fa-tasks" style={{ fontSize: '1.2rem', color: '#96ca2d' }}></i>
                                  {t('workPackages.labels.tasks')}
                                </h3>
                                <div style={{
                                  display: 'grid',
                                  gap: '15px'
                                }}>
                                  {wp.tasks && wp.tasks.map((task, index) => (
                                    <div key={task.id} style={{
                                      background: 'white',
                                      padding: '20px',
                                      borderRadius: '12px',
                                      border: '2px solid #e9ecef',
                                      boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)';
                            }}
                            onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                                    }}>
                                      <div style={{ flex: 1 }}>
                                        <h4 style={{
                                          margin: '0 0 8px 0',
                                          fontSize: '1.1rem',
                                          fontWeight: '600',
                                          color: '#2c3e50',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '10px'
                            }}>
                              <span style={{
                                              background: '#f8f9fa',
                                              color: '#6c757d',
                                              padding: '4px 8px',
                                              borderRadius: '6px',
                                              fontSize: '0.7rem',
                                              fontWeight: '600',
                                              border: '1px solid #e9ecef'
                                            }}>
                                              {task.id}
                                            </span>
                                          {task.title}
                                        </h4>
                                        <div style={{
                                          fontSize: '0.9rem',
                                          color: '#6c757d',
                                          marginBottom: '12px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px'
                                        }}>
                                          <i className="fas fa-clock" style={{ color: '#96ca2d' }}></i>
                                          {t('workPackages.labels.duration')}: {task.duration}
                      </div>
                                        {task.leadPartner && (
                                          <div style={{
                                            fontSize: '0.9rem',
                                            color: '#6c757d',
                                            marginBottom: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                          }}>
                                            <i className="fas fa-user-tie" style={{ color: '#96ca2d' }}></i>
                                            {t('workPackages.labels.leadPartner')}: {task.leadPartner}
                                          </div>
                                        )}
                                        {task.contributingPartners && (
                                          <div style={{
                                            fontSize: '0.9rem',
                                            color: '#6c757d',
                                            marginBottom: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                          }}>
                                            <i className="fas fa-users" style={{ color: '#96ca2d' }}></i>
                                            {t('workPackages.labels.contributingPartners')}: {task.contributingPartners}
                                          </div>
                                        )}
                                        <p style={{
                                          color: '#495057',
                                          fontSize: '0.95rem',
                                          lineHeight: 1.5,
                                          margin: 0,
                                          wordWrap: 'break-word',
                                          overflowWrap: 'break-word',
                                          overflow: 'visible'
                                        }}>
                                          {task.description}
                                        </p>
                    </div>
                  </div>
                ))}
                                </div>
              </div>

                              {/* Deliverables Section */}
                              <div style={{ marginBottom: '30px' }}>
                                <h3 style={{
                                  fontSize: isMobile ? '1rem' : '1.3rem',
                                  fontWeight: '600',
                                  color: '#2c3e50',
                          marginBottom: '20px',
                          display: 'flex',
                          alignItems: 'center',
                                  gap: '12px'
                                }}>
                                  <i className="fas fa-clipboard-list" style={{ fontSize: '1.2rem', color: '#96ca2d' }}></i>
                                  {t('workPackages.labels.deliverables')}
                                </h3>
                                <div style={{
                                  display: 'grid',
                                  gap: '15px'
                                }}>
                                  {wpDeliverables.length > 0 ? (
                                    wpDeliverables.map((deliverable, index) => {
                                      // Déterminer la couleur du badge de statut
                                      const getStatusColor = (status) => {
                                        switch (status) {
                                          case 'completed':
                                            return '#10b981';
                                          case 'in_progress':
                                            return '#f59e0b';
                                          case 'pending':
                                          default:
                                            return '#6b7280';
                                        }
                                      };

                                      const getStatusLabel = (status) => {
                                        switch (status) {
                                          case 'completed':
                                            return t('workPackages.deliverable.status.completed', 'Completed');
                                          case 'in_progress':
                                            return t('workPackages.deliverable.status.inProgress', 'In Progress');
                                          case 'pending':
                                          default:
                                            return t('workPackages.deliverable.status.pending', 'Pending');
                                        }
                                      };

                                      return (
                                        <div key={deliverable.id || index} style={{
                                      background: 'white',
                                      padding: '20px',
                                      borderRadius: '12px',
                                      border: '2px solid #e9ecef',
                                      boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)';
                            }}
                            onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                                    }}>
                                      <div style={{ flex: 1 }}>
                                            <div style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              marginBottom: '12px',
                                              flexWrap: 'wrap',
                                              gap: '8px'
                                            }}>
                                        <h4 style={{
                                                margin: 0,
                                          fontSize: '1.1rem',
                                          fontWeight: '600',
                                          color: '#2c3e50',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '10px'
                            }}>
                              <span style={{
                                              background: '#f8f9fa',
                                              color: '#6c757d',
                                              padding: '4px 8px',
                                              borderRadius: '6px',
                                              fontSize: '0.7rem',
                                              fontWeight: '600',
                                              border: '1px solid #e9ecef'
                                            }}>
                                                  {deliverable.id}
                                            </span>
                                                <span style={{
                                          fontSize: '0.95rem',
                                                  color: '#495057',
                                                  fontWeight: '500'
                                                }}>
                                                  {deliverable.description}
                                                </span>
                                              </h4>
                                              <span style={{
                                                background: getStatusColor(deliverable.status),
                                                color: 'white',
                                                padding: '6px 14px',
                                                borderRadius: '20px',
                                                fontSize: '0.8rem',
                                                fontWeight: '600',
                                                textTransform: 'capitalize',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                boxShadow: `0 2px 4px ${getStatusColor(deliverable.status)}40`
                                              }}>
                                                <i className={`fas ${
                                                  deliverable.status === 'completed' ? 'fa-check-circle' :
                                                  deliverable.status === 'in_progress' ? 'fa-spinner fa-spin' :
                                                  'fa-clock'
                                                }`} style={{ fontSize: '0.75rem' }}></i>
                                                {getStatusLabel(deliverable.status)}
                                              </span>
                    </div>
                                            <div style={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              gap: '12px',
                                              marginTop: '12px',
                                              fontSize: '0.85rem',
                                              color: '#6b7280'
                                            }}>
                                              {deliverable.date && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                  <i className="fas fa-calendar-alt" style={{ color: '#96ca2d' }}></i>
                                                  <span>
                                                    {t('workPackages.deliverable.date', 'Date')}: {deliverable.date}
                                                  </span>
                  </div>
                                              )}
                                              {deliverable.lead && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                  <i className="fas fa-user" style={{ color: '#96ca2d' }}></i>
                                                  <span>
                                                    {t('workPackages.deliverable.lead', 'Lead')}: {deliverable.lead}
                                                  </span>
                                                </div>
                                              )}
                                              {deliverable.file && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                  <a 
                                                    href={getFileUrl(deliverable.file)} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    download
                                                    title={t('workPackages.deliverable.download', 'Download File')}
                                                    style={{ 
                                                      color: '#ffffff',
                                                      background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                                                      padding: '8px 10px',
                                                      borderRadius: '6px',
                                                      textDecoration: 'none',
                                                      fontWeight: '600',
                                                      fontSize: '0.75rem',
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      transition: 'all 0.3s ease',
                                                      boxShadow: '0 2px 4px rgba(150, 202, 45, 0.3)',
                                                      width: '14px',
                                                      height: '10px'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(150, 202, 45, 0.4)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                      e.currentTarget.style.transform = 'translateY(0)';
                                                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(150, 202, 45, 0.3)';
                                                    }}
                                                  >
                                                    <i className="fas fa-download" style={{ fontSize: '1rem' }}></i>
                                                  </a>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <div style={{
                                      padding: '20px',
                                      textAlign: 'center',
                                      color: '#6b7280',
                                      fontSize: '0.95rem'
                                    }}>
                                      {deliverablesLoading ? (
                                        <div>
                                          <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                          {t('workPackages.deliverable.loading', 'Loading deliverables...')}
                                        </div>
                                      ) : (
                                        <div>
                                          <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                                          {t('workPackages.deliverable.noDeliverables', 'No deliverables available for this work package.')}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
              </div>
                            </>
                          );
                        })()}
                      </article>
                    )
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '400px',
                      background: '#f8f9fa',
                      borderRadius: '12px',
                      border: '2px dashed #dee2e6'
                    }}>
                      <div style={{
                        fontSize: '4rem',
                        color: '#96ca2d',
                        marginBottom: '20px'
                      }}>
                        <i className="fas fa-mouse-pointer"></i>
                      </div>
                      <h3 style={{
                        fontSize: '1.5rem',
                        fontWeight: '600',
                        color: '#2c3e50',
                        marginBottom: '10px'
                      }}>
                        {t('workPackages.selectWP')}
                      </h3>
                      <p style={{ 
                        color: '#6c757d', 
                        fontSize: '1rem',
                        textAlign: 'center',
                        maxWidth: '300px'
                      }}>
                        {t('workPackages.selectWPDescription')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

            </main>
            </div>
          </section>

          <Footer />
          </div></div></div>
      
    </>
  );
};

export default WorkPackages;
